
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import "./StudentManagement.css";

const API_URL = "http://localhost:5004";

const categories = [
    "OC", "OC-EWS", "BC-A", "BC-B", "BC-C", "BC-D",
    "BC-E", "SC-1", "SC-2", "SC-3", "ST",
];
const departments = [
    "Department of Biotechnology",
    "Department of Botany",
    "Department of Business Management",
    "Department of Chemistry",
    "Department of Commerce",
    "Department of Computer Science",
    "Department of Economics",
    "Department of English",
    "Department of Food Science and Nutrition",
    "Department of Mathematics",
    "Department of Statistics",
    "Department of Physics",
    "Department of Telugu",
    "Department of Zoology",
    "Department of Artificial Intelligence",
    "Department of Computer Science and Engineering",
    "Department of Electronics and Communication Engineering",
    "Department of Civil Engineering",
    "Department of Mechanical Engineering",
    "Department of Humanities and Social Sciences",
];

const courses = [
  // Undergraduate
  "B.Sc Computer Science",
  "B.Com Computer Applications",
  "B.Sc Horticulture",

  // Engineering
  "B.Tech CSE",
  "B.Tech AI",
  "B.Tech ECE",
  "B.Tech Civil",
  "B.Tech Mechanical",

  // Postgraduate
  "M.Sc Biotechnology",
  "M.Sc Botany",
  "M.B.A",
  "M.Sc Chemistry",
  "M.Com",
  "M.C.A",
  "M.Sc Computer Science",
  "M.A English",
  "M.A Economics",
  "M.Sc Food Science and Nutrition",
  "M.Sc Mathematics",
  "M.Sc OR & SQC (Statistics)",
  "M.Sc Physics",
  "M.A Telugu",
  "M.Sc Zoology",
];


const emptyForm = {
    admission_number: "",
    hall_ticket: "",
    full_name: "",
    gender: "",
    date_of_birth: "",
    father_name: "",
    father_mobile: "",
    email: "",
    mobile: "",
    address: "",
    aadhaar_number: "",
    college: "UNIVERSITY_COLLEGE",
    department: "",
    course: "",
    admission_year: String(new Date().getFullYear()),
    semester: "1",
    category: "",
    scholarship: "NO",
    status: "ACTIVE",
};

/* Student photo component */
function StudentPhoto({ studentId, fullName }) {
    const [photoUrl, setPhotoUrl] = useState("");
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        let objectUrl;

        async function loadPhoto() {
            try {
                const token = sessionStorage.getItem("erpToken");

                const response = await fetch(
                    `${API_URL}/api/students/${studentId}/photo`,
                    {
                        headers: {
                            Authorization: `Bearer ${token || ""}`,
                        },
                        signal: controller.signal,
                    }
                );

                if (!response.ok) {
                    setFailed(true);
                    return;
                }

                const blob = await response.blob();

                if (!blob.type.startsWith("image/")) {
                    setFailed(true);
                    return;
                }

                objectUrl = URL.createObjectURL(blob);
                setPhotoUrl(objectUrl);
            } catch (err) {
                if (err.name !== "AbortError") {
                    setFailed(true);
                }
            }
        }

        loadPhoto();

        return () => {
            controller.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [studentId]);

    return (
        <Avatar
            src={photoUrl || undefined}
            alt={fullName}
            variant="rounded"
            sx={{
                width: 44,
                height: 48,
                bgcolor: "#e9f0fb",
                color: "#164b83",
                fontWeight: 700,
            }}
        >
            {failed || !photoUrl
                ? fullName?.trim()?.charAt(0)?.toUpperCase() || "?"
                : null}
        </Avatar>
    );
}

export default function StudentManagement() {
    const navigate = useNavigate();

    const [form, setForm] = useState({ ...emptyForm });
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const [students, setStudents] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [editId, setEditId] = useState(null);
    const [deactivateStudent, setDeactivateStudent] = useState(null);
    const [deactivating, setDeactivating] = useState(false);

    /* Fetch student records */
    const fetchStudents = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const token = sessionStorage.getItem("erpToken");

            const response = await fetch(
                `${API_URL}/api/students?search=${encodeURIComponent(search)}`,
                {
                    headers: {
                        Authorization: `Bearer ${token || ""}`,
                    },
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(
                    data.message || "Unable to load student records."
                );
            }

            setStudents(data.students || []);
        } catch (err) {
            setError(
                err.message ||
                "Cannot connect to the ERP server. Check whether it is running."
            );
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        fetchStudents();
    }, [fetchStudents]);

    useEffect(() => {
        return () => {
            if (photoPreview) {
                URL.revokeObjectURL(photoPreview);
            }
        };
    }, [photoPreview]);

    /* Handle form fields */
    const handleChange = (event) => {
        const { name, value } = event.target;

        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    /* Validate and preview profile photo */
    const handlePhotoChange = (event) => {
        const selectedFile = event.target.files?.[0];

        if (!selectedFile) return;

        if (
            !["image/jpeg", "image/png", "image/webp"].includes(
                selectedFile.type
            )
        ) {
            setError("Please select a JPG, PNG, or WEBP image.");
            event.target.value = "";
            return;
        }

        if (selectedFile.size > 5 * 1024 * 1024) {
            setError("Profile photo must be 5 MB or smaller.");
            event.target.value = "";
            return;
        }

        setError("");
        setPhoto(selectedFile);
        setPhotoPreview(URL.createObjectURL(selectedFile));
    };

    /* Clear form */
    const resetForm = () => {
        setForm({ ...emptyForm });
        setPhoto(null);
        setPhotoPreview("");
        setEditId(null);
        setError("");
        setMessage("");

        const photoInput = document.getElementById(
            "student-profile-photo"
        );

        if (photoInput) {
            photoInput.value = "";
        }
    };

    /* Prefill the form for editing */
    const handleEdit = (student) => {
        setError("");
        setMessage("");
        setEditId(student.student_id);

        setForm({
            admission_number: student.admission_number || "",
            hall_ticket: student.hall_ticket || "",
            full_name: student.full_name || "",
            gender: student.gender || "",
            date_of_birth: student.date_of_birth
                ? String(student.date_of_birth).slice(0, 10)
                : "",
            father_name: student.father_name || "",
            father_mobile: student.father_mobile || "",
            email: student.email || "",
            mobile: student.mobile || "",
            address: student.address || "",
            aadhaar_number: student.aadhaar_number || "",
            college: student.college || "UNIVERSITY_COLLEGE",
            department: student.department || "",
            course: student.course || "",
            admission_year: String(student.admission_year || ""),
            semester: String(student.semester || "1"),
            category: student.category || "",
            scholarship: student.scholarship || "NO",
            status: student.status || "ACTIVE",
        });

        setPhoto(null);
        setPhotoPreview("");

        const photoInput = document.getElementById(
            "student-profile-photo"
        );

        if (photoInput) {
            photoInput.value = "";
        }

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    /* Save new or edited student */
    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");
        setMessage("");

        if (!editId && !photo) {
            setError("Please select a profile photo.");
            return;
        }

        if (!/^\d{12}$/.test(form.aadhaar_number)) {
            setError(
                "Please enter a valid 12-digit Aadhaar number."
            );
            return;
        }

        if (!/^[0-9+\-\s]{10,15}$/.test(form.mobile)) {
            setError("Please enter a valid student mobile number.");
            return;
        }

        if (
            form.father_mobile &&
            !/^[0-9+\-\s]{10,15}$/.test(form.father_mobile)
        ) {
            setError("Please enter a valid father's mobile number.");
            return;
        }

        setSaving(true);

        try {
            const token = sessionStorage.getItem("erpToken");
            const formData = new FormData();

            Object.entries(form).forEach(([key, value]) => {
                formData.append(key, value ?? "");
            });

            if (photo) {
                formData.append("profile_photo", photo);
            }

            const isEditing = Boolean(editId);

            const response = await fetch(
                isEditing
                    ? `${API_URL}/api/students/${editId}`
                    : `${API_URL}/api/students`,
                {
                    method: isEditing ? "PUT" : "POST",
                    headers: {
                        Authorization: `Bearer ${token || ""}`,
                    },
                    body: formData,
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(
                    data.message ||
                    (isEditing
                        ? "Unable to update student."
                        : "Student registration failed.")
                );
            }

            setMessage(
                data.message ||
                (isEditing
                    ? "Student updated successfully."
                    : "Student registered successfully.")
            );

            resetForm();
            await fetchStudents();
        } catch (err) {
            setError(err.message || "Unable to save student details.");
        } finally {
            setSaving(false);
        }
    };

    /* Deactivate student after confirmation */
    const handleDeactivate = async () => {
        if (!deactivateStudent) return;

        setDeactivating(true);
        setError("");
        setMessage("");

        try {
            const token = sessionStorage.getItem("erpToken");

            const response = await fetch(
                `${API_URL}/api/students/${deactivateStudent.student_id}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token || ""}`,
                    },
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(
                    data.message || "Unable to deactivate student."
                );
            }

            if (editId === deactivateStudent.student_id) {
                resetForm();
            }

            setDeactivateStudent(null);
            setMessage(
                data.message || "Student deactivated successfully."
            );

            await fetchStudents();
        } catch (err) {
            setError(err.message || "Unable to deactivate student.");
        } finally {
            setDeactivating(false);
        }
    };

    return (
        <Box className="student-management-page">
            <Container maxWidth="xl">
                <Button
                    startIcon={<ArrowBackOutlinedIcon />}
                    onClick={() =>
                        navigate("/student-erp/admin-dashboard")
                    }
                    className="student-management-back"
                >
                    Back to Dashboard
                </Button>

                <Box className="student-management-heading">
                    <Typography variant="h4" component="h1">
                        Student Management
                    </Typography>

                    <Typography>
                        Student admission, personal details and academic records
                    </Typography>
                </Box>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        onClose={() => setError("")}
                    >
                        {error}
                    </Alert>
                )}

                {message && (
                    <Alert
                        severity="success"
                        sx={{ mb: 2 }}
                        onClose={() => setMessage("")}
                    >
                        {message}
                    </Alert>
                )}

                {/* Registration and editing form */}
                <Paper className="student-management-form" elevation={0}>
                    <Typography
                        variant="h6"
                        className="student-management-form-title"
                    >
                        {editId ? (
                            <SaveOutlinedIcon
                                sx={{ verticalAlign: "middle", mr: 1 }}
                            />
                        ) : (
                            <PersonAddAltOutlinedIcon
                                sx={{ verticalAlign: "middle", mr: 1 }}
                            />
                        )}

                        {editId
                            ? "Edit Student Details"
                            : "Register New Student"}
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit}>
                        <Typography className="student-form-section">
                            1. Admission Details
                        </Typography>

                        <Box className="student-management-fields">
                            <TextField
                                label="Admission Number *"
                                name="admission_number"
                                value={form.admission_number}
                                onChange={handleChange}
                                required
                                fullWidth
                            />

                            <TextField
                                label="Hall-ticket Number *"
                                name="hall_ticket"
                                value={form.hall_ticket}
                                onChange={handleChange}
                                required
                                fullWidth
                            />

                            <TextField
                                select
                                label="College *"
                                name="college"
                                value={form.college}
                                onChange={handleChange}
                                required
                                fullWidth
                            >
                                <MenuItem value="UNIVERSITY_COLLEGE">
                                    University College
                                </MenuItem>
                                <MenuItem value="ENGINEERING_COLLEGE">
                                    Engineering College
                                </MenuItem>
                            </TextField>

                            <TextField
                                label="Admission Year *"
                                name="admission_year"
                                type="number"
                                value={form.admission_year}
                                onChange={handleChange}
                                inputProps={{ min: 2000, max: 2100 }}
                                required
                                fullWidth
                            />

                            <TextField
                                select
                                label="Department *"
                                name="department"
                                value={form.department}
                                onChange={handleChange}
                                required
                                fullWidth
                            >
                                {departments.map((department) => (
                                    <MenuItem key={department} value={department}>
                                        {department}
                                    </MenuItem>
                                ))}
                            </TextField>


                            <TextField
                                select
                                label="Course *"
                                name="course"
                                value={form.course}
                                onChange={handleChange}
                                required
                                fullWidth
                            >
                                {courses.map((course) => (
                                    <MenuItem key={course} value={course}>
                                        {course}
                                    </MenuItem>
                                ))}
                            </TextField>


                            <TextField
                                select
                                label="Semester *"
                                name="semester"
                                value={form.semester}
                                onChange={handleChange}
                                required
                                fullWidth
                            >
                                {[1, 2, 3, 4, 5, 6, 7, 8].map((semester) => (
                                    <MenuItem
                                        key={semester}
                                        value={String(semester)}
                                    >
                                        Semester {semester}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>

                        <Typography className="student-form-section">
                            2. Personal Details
                        </Typography>

                        <Box className="student-management-fields">
                            <TextField
                                label="Student Full Name *"
                                name="full_name"
                                value={form.full_name}
                                onChange={handleChange}
                                required
                                fullWidth
                            />

                            <TextField
                                select
                                label="Gender *"
                                name="gender"
                                value={form.gender}
                                onChange={handleChange}
                                required
                                fullWidth
                            >
                                <MenuItem value="MALE">Male</MenuItem>
                                <MenuItem value="FEMALE">Female</MenuItem>
                                <MenuItem value="OTHER">Other</MenuItem>
                            </TextField>

                            <TextField
                                label="Date of Birth *"
                                name="date_of_birth"
                                type="date"
                                value={form.date_of_birth}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    max: new Date().toISOString().slice(0, 10),
                                }}
                                required
                                fullWidth
                            />

                            <TextField
                                label="Aadhaar Number *"
                                name="aadhaar_number"
                                value={form.aadhaar_number}
                                onChange={(event) => {
                                    const value = event.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 12);

                                    setForm((previous) => ({
                                        ...previous,
                                        aadhaar_number: value,
                                    }));
                                }}
                                inputProps={{
                                    inputMode: "numeric",
                                    maxLength: 12,
                                }}
                                helperText="Enter 12 digits. Restrict access to this information."
                                required
                                fullWidth
                            />

                            <TextField
                                label="Student Mobile *"
                                name="mobile"
                                value={form.mobile}
                                onChange={handleChange}
                                required
                                fullWidth
                            />

                            <TextField
                                label="Email"
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={handleChange}
                                fullWidth
                            />

                            <TextField
                                label="Address *"
                                name="address"
                                value={form.address}
                                onChange={handleChange}
                                required
                                multiline
                                minRows={2}
                                fullWidth
                                sx={{ gridColumn: { md: "span 2" } }}
                            />
                        </Box>

                        <Typography className="student-form-section">
                            3. Parent Details
                        </Typography>

                        <Box className="student-management-fields">
                            <TextField
                                label="Father's Name *"
                                name="father_name"
                                value={form.father_name}
                                onChange={handleChange}
                                required
                                fullWidth
                            />

                            <TextField
                                label="Father's Mobile Number"
                                name="father_mobile"
                                value={form.father_mobile}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Box>

                        <Typography className="student-form-section">
                            4. Reservation and Scholarship
                        </Typography>

                        <Box className="student-management-fields">
                            <TextField
                                select
                                label="Category *"
                                name="category"
                                value={form.category}
                                onChange={handleChange}
                                required
                                fullWidth
                            >
                                {categories.map((category) => (
                                    <MenuItem key={category} value={category}>
                                        {category}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                label="Scholarship *"
                                name="scholarship"
                                value={form.scholarship}
                                onChange={handleChange}
                                required
                                fullWidth
                            >
                                <MenuItem value="YES">Yes</MenuItem>
                                <MenuItem value="NO">No</MenuItem>
                            </TextField>

                            {editId && (
                                <TextField
                                    select
                                    label="Student Status *"
                                    name="status"
                                    value={form.status}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                >
                                    <MenuItem value="ACTIVE">Active</MenuItem>
                                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                                    <MenuItem value="GRADUATED">Graduated</MenuItem>
                                </TextField>
                            )}
                        </Box>

                        <Typography className="student-form-section">
                            5. Student Profile Photo
                        </Typography>

                        <Box className="student-photo-upload">
                            <Avatar
                                src={photoPreview || undefined}
                                variant="rounded"
                                className="student-photo-preview"
                            >
                                <PhotoCameraOutlinedIcon />
                            </Avatar>

                            <Box>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<PhotoCameraOutlinedIcon />}
                                >
                                    {editId
                                        ? "Choose Replacement Photo (Optional)"
                                        : "Choose Profile Photo"}

                                    <input
                                        id="student-profile-photo"
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        hidden
                                        onChange={handlePhotoChange}
                                    />
                                </Button>

                                <Typography className="student-photo-help">
                                    JPG, PNG or WEBP. Maximum size: 5 MB.
                                </Typography>

                                {photo && (
                                    <Typography className="student-photo-filename">
                                        Selected: {photo.name}
                                    </Typography>
                                )}

                                {editId && !photo && (
                                    <Typography className="student-photo-help">
                                        Existing photo will be retained if no replacement is selected.
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Box className="student-form-actions">
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={editId ? resetForm : resetForm}
                                disabled={saving}
                            >
                                {editId ? "Cancel Edit" : "Clear Form"}
                            </Button>

                            <Button
                                type="submit"
                                variant="contained"
                                disabled={saving}
                                startIcon={
                                    editId ? (
                                        <SaveOutlinedIcon />
                                    ) : (
                                        <PersonAddAltOutlinedIcon />
                                    )
                                }
                            >
                                {saving
                                    ? "Saving..."
                                    : editId
                                        ? "Update Student"
                                        : "Register Student"}
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Registered students list */}
                <Paper className="student-management-list" elevation={0}>
                    <Box className="student-management-list-heading">
                        <Box>
                            <Typography variant="h6">
                                Registered Students
                            </Typography>

                            <Typography className="student-record-count">
                                {students.length} record(s) displayed
                            </Typography>
                        </Box>

                        <Box className="student-list-tools">
                            <TextField
                                size="small"
                                label="Search students"
                                placeholder="Name, admission no. or hall ticket"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                            />

                            <Button
                                variant="outlined"
                                startIcon={<RefreshOutlinedIcon />}
                                onClick={fetchStudents}
                                disabled={loading}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Box>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Photo</TableCell>
                                    <TableCell>Admission No.</TableCell>
                                    <TableCell>Hall Ticket</TableCell>
                                    <TableCell>Student Name</TableCell>
                                    <TableCell>College</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell>Course</TableCell>
                                    <TableCell>Semester</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Scholarship</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {students.map((student) => (
                                    <TableRow
                                        key={student.student_id}
                                        hover
                                    >
                                        <TableCell>
                                            <StudentPhoto
                                                studentId={student.student_id}
                                                fullName={student.full_name}
                                            />
                                        </TableCell>

                                        <TableCell>
                                            {student.admission_number || "—"}
                                        </TableCell>

                                        <TableCell>{student.hall_ticket}</TableCell>
                                        <TableCell>{student.full_name}</TableCell>

                                        <TableCell>
                                            {student.college === "UNIVERSITY_COLLEGE"
                                                ? "University College"
                                                : "Engineering College"}
                                        </TableCell>

                                        <TableCell>{student.department}</TableCell>
                                        <TableCell>{student.course}</TableCell>
                                        <TableCell>{student.semester}</TableCell>
                                        <TableCell>{student.category || "—"}</TableCell>
                                        <TableCell>{student.scholarship || "NO"}</TableCell>
                                        <TableCell>{student.status}</TableCell>

                                        <TableCell>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    gap: 1,
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<EditOutlinedIcon />}
                                                    onClick={() => handleEdit(student)}
                                                >
                                                    Edit
                                                </Button>

                                                <Button
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                    startIcon={<DeleteOutlineOutlinedIcon />}
                                                    disabled={student.status === "INACTIVE"}
                                                    onClick={() =>
                                                        setDeactivateStudent(student)
                                                    }
                                                >
                                                    Deactivate
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {!loading && students.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={12} align="center">
                                            No student records found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {loading && (
                        <Typography sx={{ p: 2 }}>
                            Loading student records...
                        </Typography>
                    )}
                </Paper>

                {/* Confirmation dialog */}
                <Dialog
                    open={Boolean(deactivateStudent)}
                    onClose={() => {
                        if (!deactivating) {
                            setDeactivateStudent(null);
                        }
                    }}
                    aria-labelledby="deactivate-student-title"
                >
                    <DialogTitle id="deactivate-student-title">
                        Confirm Student Deactivation
                    </DialogTitle>

                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to deactivate{" "}
                            <strong>{deactivateStudent?.full_name}</strong>{" "}
                            ({deactivateStudent?.hall_ticket})? The record will
                            remain in the database, but its status will change
                            to INACTIVE.
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            onClick={() => setDeactivateStudent(null)}
                            disabled={deactivating}
                        >
                            Cancel
                        </Button>

                        <Button
                            color="error"
                            variant="contained"
                            onClick={handleDeactivate}
                            disabled={deactivating}
                        >
                            {deactivating
                                ? "Deactivating..."
                                : "Confirm Deactivation"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
}
